<!-- @format -->

<template>
	<div class="gestor-ot">
		<div class="gestor-ot__cabecera">
			<h3 class="gestor-ot__titulo mr-3">Órdenes de trabajo</h3>
			<v-spacer />
			<v-btn
				small
				text
				class="text-none"
				:loading="loadingOrdenes"
				@click="buscar({ pagina: 1 })"
			>
				<v-icon left small>mdi-refresh</v-icon>
				Actualizar
			</v-btn>
			<v-btn small text class="text-none" :loading="exportando" @click="exportarExcel">
				<v-icon left small>mdi-microsoft-excel</v-icon>
				Excel
			</v-btn>
			<v-btn small text class="text-none d-none d-md-inline-flex" @click="imprimir">
				<v-icon left small>mdi-printer-outline</v-icon>
				Imprimir
			</v-btn>
		</div>

		<work-order-kpis
			class="no-imprimir"
			:kpis="kpisOrdenes"
			:activo="filtro.estado"
			:cargando="loadingOrdenes"
			:mostrar-firma="!modoDegradado"
			@filtrar="estado => buscar({ estado, pagina: 1 })"
		/>

		<work-order-filters
			class="no-imprimir"
			:filtro="filtro"
			:tecnicos="listPerson"
			:vista="vista"
			:resumen="resumen"
			@cambiar="cambiarFiltro"
			@limpiar="limpiarFiltros"
			@vista="cambiarVista"
		/>

		<div class="caja-meses no-imprimir" :class="{ 'caja-meses--suelta': vistaCompacta }">
			<work-order-month-tabs
				:valor="filtro.mes"
				:conteos="conteosOrdenesPorMes"
				:cargando="loadingOrdenes"
				@input="seleccionarMes"
				@anio="cargarConteos"
			/>
		</div>

		<v-alert v-if="modoDegradado" dense text type="warning" class="mt-2 caption no-imprimir">
			No se pudo consultar el buscador; se muestran las órdenes del mes ya cargadas.
		</v-alert>

		<work-order-table
			v-if="!vistaCompacta"
			:ordenes="ordenesVisibles"
			:seleccion="seleccion"
			:total="totalFilas"
			:opciones="opcionesTabla"
			:cargando="loadingOrdenes"
			:descargando="descargando"
			:compartiendo="compartiendo"
			@seleccionar="valor => (seleccion = valor)"
			@opciones="cambiarOpcionesTabla"
			@compartir="compartirUna"
			@descargar="descargarUna"
			@copiar="copiarEnlace"
		/>

		<work-order-cards
			v-else
			class="mt-2"
			:ordenes="ordenesVisibles"
			:seleccion="seleccion"
			:cargando="loadingOrdenes"
			:descargando="descargando"
			:compartiendo="compartiendo"
			:dos-columnas="tarjetasEnDosColumnas"
			@alternar="alternarSeleccion"
			@compartir="compartirUna"
			@descargar="descargarUna"
			@copiar="copiarEnlace"
		/>

		<work-order-paginador
			v-if="!vistaCompacta && !modoDegradado"
			class="no-imprimir"
			:pagina="filtro.pagina"
			:cantidad="filtro.cantidad"
			:total="totalFilas"
			:total-general="totalGeneralOrdenes"
			:cargando="loadingOrdenes"
			@pagina="pagina => buscar({ pagina })"
			@cantidad="cantidad => buscar({ cantidad, pagina: 1 })"
		/>

		<div
			v-if="vistaCompacta && ordenesVisibles.length < totalFilas"
			class="text-center my-3 no-imprimir"
		>
			<v-btn text color="primary" class="text-none" @click="paginaSiguiente">
				Cargar más ({{ ordenesVisibles.length }} de {{ totalFilas }})
			</v-btn>
		</div>

		<work-order-bulk-bar
			class="no-imprimir"
			:cantidad="seleccion.length"
			@limpiar="seleccion = []"
			@descargar="descargarLote"
			@compartir="compartirLote"
			@excel="exportarExcel"
		/>

		<work-order-bulk-progress
			:valor="lote.abierto"
			:titulo="lote.titulo"
			:hechas="lote.hechas"
			:total="lote.total"
			:errores="lote.errores"
			:terminado="lote.terminado"
			@cancelar="lote.cancelado = true"
			@cerrar="cerrarLote"
			@reintentar="reintentarLote"
		/>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import moment from 'moment';
import { debounce } from 'lodash';
import { mapearOrdenes, construirDesdeLodgings } from '@/utils/workOrders/mapear';
import { descargarPdfDeOT, urlAbsolutaOT } from '@/utils/workOrders/descargas';
import { exportarOrdenesAExcel } from '@/utils/workOrders/excel';
import { ejecutarEnLotes } from '@/utils/concurrencia';
import { leerPreferencia, guardarPreferencia } from '@/utils/preferencias';

const CANTIDAD_TARJETAS = 20;

export default {
	name: 'ListWorkOrders',
	components: {
		WorkOrderMonthTabs: () => import('./workorders/WorkOrderMonthTabs.vue'),
		WorkOrderKpis: () => import('./workorders/WorkOrderKpis.vue'),
		WorkOrderFilters: () => import('./workorders/WorkOrderFilters.vue'),
		WorkOrderTable: () => import('./workorders/WorkOrderTable.vue'),
		WorkOrderCards: () => import('./workorders/WorkOrderCards.vue'),
		WorkOrderPaginador: () => import('./workorders/WorkOrderPaginador.vue'),
		WorkOrderBulkBar: () => import('./workorders/WorkOrderBulkBar.vue'),
		WorkOrderBulkProgress: () => import('./workorders/WorkOrderBulkProgress.vue'),
	},
	data() {
		return {
			seleccion: [],
			descargando: '',
			compartiendo: '',
			exportando: false,
			vista: leerPreferencia('vista', 'auto'),
			modoDegradado: false,
			lote: {
				abierto: false,
				titulo: '',
				hechas: 0,
				total: 0,
				errores: [],
				terminado: false,
				cancelado: false,
				pendientes: [],
				accion: '',
			},
		};
	},
	computed: {
		filtro() {
			return this.filtroOrdenes;
		},
		vistaCompacta() {
			if (this.vista === 'tarjetas') return true;
			if (this.vista === 'tabla') return false;
			// bajo 1264px la tabla completa ya no cabe sin scroll horizontal
			return this.$vuetify.breakpoint.width < 1264;
		},
		tarjetasEnDosColumnas() {
			return this.$vuetify.breakpoint.width >= 900;
		},
		ordenesEnriquecidas() {
			if (this.modoDegradado)
				return construirDesdeLodgings({
					lodgings: this.lodgings,
					places: this.places,
					personas: this.listPerson,
				});
			return mapearOrdenes(this.ordenes);
		},
		ordenesVisibles() {
			return this.ordenesEnriquecidas;
		},
		totalFilas() {
			return this.modoDegradado ? this.ordenesEnriquecidas.length : this.totalOrdenes;
		},
		opcionesTabla() {
			return {
				page: this.filtro.pagina,
				itemsPerPage: this.filtro.cantidad,
				sortBy: [this.filtro.orden === 'folio' ? 'folio' : 'fechaActividad'],
				sortDesc: [this.filtro.direccion === 'desc'],
			};
		},
		resumen() {
			if (this.loadingOrdenes) return 'Buscando…';
			const totalMes = this.totalGeneralOrdenes;
			if (this.totalFilas === totalMes) return `${totalMes} órdenes`;
			return `${this.totalFilas} de ${totalMes} órdenes`;
		},
		...mapGetters({
			listPerson: 'Person/persons',
			lodgings: 'Lodging/allLodgings',
			places: 'Place/places',
			month: 'Lodging/month',
			ordenes: 'Lodging/ordenes',
			totalOrdenes: 'Lodging/totalOrdenes',
			totalGeneralOrdenes: 'Lodging/totalGeneralOrdenes',
			kpisOrdenes: 'Lodging/kpisOrdenes',
			conteosOrdenesPorMes: 'Lodging/conteosOrdenesPorMes',
			loadingOrdenes: 'Lodging/loadingOrdenes',
			errorOrdenes: 'Lodging/errorOrdenes',
			filtroOrdenes: 'Lodging/filtroOrdenes',
		}),
	},
	watch: {
		month(nuevo) {
			// el mes es estado compartido con la planificación
			if (nuevo && nuevo !== this.filtro.mes) this.buscar({ mes: nuevo, pagina: 1 });
		},
		errorOrdenes(nuevo) {
			this.modoDegradado = !!nuevo;
		},
	},
	created() {
		const guardado = leerPreferencia('filtros', null);
		const inicial = { mes: this.month, pagina: 1 };
		if (guardado) Object.assign(inicial, guardado, { mes: this.month, pagina: 1 });
		this.setFiltroOrdenes(inicial);
	},
	mounted() {
		this.buscar();
		this.cargarConteos(this.filtro.mes.slice(0, 4));
	},
	methods: {
		async buscar(cambios = {}) {
			this.seleccion = [];
			const resultado = await this.buscarOrdenes(cambios);
			if (resultado) this.modoDegradado = false;
			this.guardarFiltros();
		},
		buscarConRetraso: debounce(function() {
			this.buscar({ pagina: 1 });
		}, 350),
		cambiarFiltro(cambios) {
			this.setFiltroOrdenes({ ...cambios, pagina: 1 });
			if ('search' in cambios) this.buscarConRetraso();
			else this.buscar({ pagina: 1 });
		},
		limpiarFiltros() {
			this.buscar({
				search: '',
				tipos: [],
				persona: '',
				desde: '',
				hasta: '',
				estado: 'todas',
				mes: this.month,
				pagina: 1,
			});
		},
		seleccionarMes(mes) {
			// setMonth dispara la recarga de la planificación del padre
			this.setMonth(mes);
			this.buscar({ mes, desde: '', hasta: '', pagina: 1 });
		},
		async cargarConteos(anio) {
			await this.obtenerConteosOrdenes(anio || this.filtro.mes.slice(0, 4));
		},
		cambiarOpcionesTabla(opciones) {
			const cambios = {};
			if (opciones.page && opciones.page !== this.filtro.pagina)
				cambios.pagina = opciones.page;
			if (opciones.itemsPerPage && opciones.itemsPerPage !== this.filtro.cantidad) {
				cambios.cantidad = opciones.itemsPerPage;
				cambios.pagina = 1;
			}
			const columna = (opciones.sortBy || [])[0];
			if (columna) {
				const orden =
					{ folio: 'folio', fechaCertificado: 'certificado', fechaOT: 'ot' }[columna] ||
					'fecha';
				const direccion = (opciones.sortDesc || [])[0] === false ? 'asc' : 'desc';
				if (orden !== this.filtro.orden || direccion !== this.filtro.direccion) {
					cambios.orden = orden;
					cambios.direccion = direccion;
					cambios.pagina = 1;
				}
			}
			if (Object.keys(cambios).length) this.buscar(cambios);
		},
		paginaSiguiente() {
			this.setFiltroOrdenes({ cantidad: this.filtro.cantidad + CANTIDAD_TARJETAS });
			this.buscarOrdenes({});
		},
		cambiarVista(valor) {
			this.vista = valor;
			guardarPreferencia('vista', valor);
		},
		guardarFiltros() {
			const {
				search,
				tipos,
				persona,
				desde,
				hasta,
				estado,
				orden,
				direccion,
				cantidad,
			} = this.filtro;
			guardarPreferencia('filtros', {
				search,
				tipos,
				persona,
				desde,
				hasta,
				estado,
				orden,
				direccion,
				cantidad,
			});
		},
		alternarSeleccion({ orden, valor }) {
			if (valor) this.seleccion = [...this.seleccion, orden];
			else this.seleccion = this.seleccion.filter(item => item.clave !== orden.clave);
		},
		async copiarEnlace(orden) {
			const enlace = urlAbsolutaOT(orden.urlVer);
			try {
				await navigator.clipboard.writeText(enlace);
				this.avisar('Enlace copiado', 'success');
			} catch (e) {
				window.prompt('Copie el enlace de la OT', enlace);
			}
		},
		async descargarUna(orden) {
			this.descargando = orden.clave;
			try {
				await descargarPdfDeOT(orden);
			} catch (error) {
				this.avisar(`No se pudo generar el PDF de la OT ${orden.folio}`, 'error');
			} finally {
				this.descargando = '';
			}
		},
		async compartirUna(orden) {
			if (orden.compartida) return this.avisar('Esta OT ya está en el portal', 'info');
			this.compartiendo = orden.clave;
			const ok = await this.publicarEnPortal(orden);
			this.compartiendo = '';
			if (ok) {
				this.marcarOrdenesCompartidas([orden.clave]);
				this.avisar('Publicada en el portal del cliente', 'success');
			} else this.avisar('No se pudo compartir la OT', 'error');
			return ok;
		},
		async publicarEnPortal(orden) {
			const persona = this.listPerson.find(item => item._id === orden.personaId);
			return this.setDocumento({
				tipo: orden.tipo,
				enlace: orden.urlVer,
				establecimiento: orden.placeId,
				nombreEstablecimiento: orden.lugar,
				fecha: orden.fechaCertificado || orden.fechaOT,
				folio: orden.folio,
				lodging: orden.lodId,
				persona: persona ? persona._id : orden.personaId,
				nombrePersona: orden.tecnico,
			});
		},
		iniciarLote(titulo, items, accion) {
			this.lote = {
				abierto: true,
				titulo,
				hechas: 0,
				total: items.length,
				errores: [],
				terminado: false,
				cancelado: false,
				pendientes: items,
				accion,
			};
		},
		cerrarLote() {
			this.lote.abierto = false;
		},
		reintentarLote() {
			const fallidas = this.lote.errores.map(error => error.orden);
			if (!fallidas.length) return;
			if (this.lote.accion === 'descarga') this.descargarLote(fallidas);
			else this.compartirLote(fallidas);
		},
		async descargarLote(ordenes) {
			const items = (Array.isArray(ordenes) ? ordenes : this.seleccion).filter(
				orden => orden.puedeVer
			);
			if (!items.length) return;
			if (
				items.length > 20 &&
				!window.confirm(
					`Se generarán ${items.length} PDF, uno por uno. Puede tardar varios minutos. ¿Continuar?`
				)
			)
				return;

			this.iniciarLote('Descargando PDF', items, 'descarga');
			const resultados = await ejecutarEnLotes(
				items,
				async orden => {
					await descargarPdfDeOT(orden);
					// respiro entre descargas: el navegador bloquea las ráfagas
					await new Promise(resolver => window.setTimeout(resolver, 300));
				},
				{
					concurrencia: 2,
					cancelado: () => this.lote.cancelado,
					onProgreso: hechas => (this.lote.hechas = hechas),
				}
			);
			this.terminarLote(resultados);
		},
		async compartirLote(ordenes) {
			const items = (Array.isArray(ordenes) ? ordenes : this.seleccion).filter(
				orden => !orden.compartida
			);
			if (!items.length)
				return this.avisar('Las OT seleccionadas ya están en el portal', 'info');

			this.iniciarLote('Compartiendo al portal', items, 'compartir');
			const compartidas = [];
			const resultados = await ejecutarEnLotes(
				items,
				async orden => {
					const ok = await this.publicarEnPortal(orden);
					if (!ok) throw new Error('rechazada por el servidor');
					compartidas.push(orden.clave);
				},
				{
					concurrencia: 3,
					cancelado: () => this.lote.cancelado,
					onProgreso: hechas => (this.lote.hechas = hechas),
				}
			);
			if (compartidas.length) this.marcarOrdenesCompartidas(compartidas);
			this.terminarLote(resultados);
		},
		terminarLote(resultados) {
			this.lote.errores = resultados
				.filter(resultado => !resultado.ok)
				.map(resultado => ({
					clave: resultado.item.clave,
					folio: resultado.item.folio,
					orden: resultado.item,
					motivo: this.motivoError(resultado.error),
				}));
			this.lote.terminado = true;
			const correctas = resultados.length - this.lote.errores.length;
			if (!this.lote.errores.length) {
				this.lote.abierto = false;
				this.avisar(`${correctas} órdenes procesadas`, 'success');
			}
		},
		motivoError(error) {
			if (!error) return 'error desconocido';
			if (error.code === 'ECONNABORTED') return 'tardó demasiado';
			if (error.response) return `error ${error.response.status}`;
			return error.message || 'error desconocido';
		},
		async exportarExcel() {
			let datos = this.seleccion;
			if (!datos.length) {
				// sin seleccion se exporta todo lo filtrado, no solo la pagina visible
				if (this.modoDegradado) datos = this.ordenesEnriquecidas;
				else {
					this.exportando = true;
					const { filas, total } = await this.obtenerOrdenesParaExportar();
					this.exportando = false;
					datos = mapearOrdenes(filas);
					if (total > filas.length)
						this.avisar(
							`Se exportan las primeras ${filas.length} de ${total} órdenes`,
							'info'
						);
				}
			}
			if (!datos.length) return this.avisar('No hay órdenes para exportar', 'info');
			const cantidad = exportarOrdenesAExcel(
				datos,
				`ordenes-${this.filtro.mes || moment().format('YYYY-MM')}`
			);
			this.avisar(`${cantidad} órdenes exportadas`, 'success');
		},
		imprimir() {
			// la clase deja visible solo el gestor mientras dura la impresion
			document.body.classList.add('imprimiendo-gestor');
			window.setTimeout(() => {
				window.print();
				document.body.classList.remove('imprimiendo-gestor');
			}, 100);
		},
		avisar(content, color) {
			this.$store.commit('Snackbar/showMessage', { content, color });
		},
		...mapActions({
			buscarOrdenes: 'Lodging/buscarOrdenes',
			obtenerConteosOrdenes: 'Lodging/obtenerConteosOrdenes',
			obtenerOrdenesParaExportar: 'Lodging/obtenerOrdenesParaExportar',
			setDocumento: 'Place/compatirDocumento',
		}),
		...mapMutations({
			setMonth: 'Lodging/setMonth',
			setFiltroOrdenes: 'Lodging/setFiltroOrdenes',
			marcarOrdenesCompartidas: 'Lodging/marcarOrdenesCompartidas',
		}),
	},
};
</script>

<style lang="scss" scoped>
.gestor-ot {
	position: relative;
	text-align: center;
}

/* Cabecera y contenido comparten la caja de 1500px de la tabla */
.gestor-ot__cabecera {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	max-width: 1500px;
	margin: 0 auto 8px;
	padding: 0 13px;
}

.gestor-ot__titulo {
	font-size: 16px;
	font-weight: 600;
	color: #37474f;
}

/* Caja de las pestañas de mes: mismo ancho y borde que la tabla, y SIN borde
   abajo, para que quede pegada a ella y se lea como una sola pieza */
.caja-meses {
	max-width: 1500px;
	margin: 12px auto 0;
	padding: 8px 12px 0;
	background-color: #fff;
	border: 1px solid #cad8dd;
	border-bottom: none;
	border-radius: 10px 10px 0 0;
	text-align: left;
	overflow-x: auto;
}

/* Con tarjetas no hay tabla debajo: la caja se cierra sola */
.caja-meses--suelta {
	border-bottom: 1px solid #cad8dd;
	border-radius: 10px;
	padding-bottom: 8px;
	margin-bottom: 8px;
}

@media (max-width: 599px) {
	.gestor-ot__cabecera {
		padding: 0 8px;
	}

	.caja-meses {
		padding: 8px 6px 0;
	}
}

@media print {
	.no-imprimir {
		display: none !important;
	}
}
</style>

<!-- Sin scoped a proposito: al imprimir hay que ocultar el resto de la pagina
	 (menu, planificacion, clientes), que vive fuera de este componente.
	 Solo se aplica mientras el metodo imprimir() pone la clase en el body. -->
<style>
@media print {
	body.imprimiendo-gestor * {
		visibility: hidden !important;
	}
	body.imprimiendo-gestor .gestor-ot,
	body.imprimiendo-gestor .gestor-ot * {
		visibility: visible !important;
	}
	body.imprimiendo-gestor .gestor-ot .no-imprimir,
	body.imprimiendo-gestor .gestor-ot .no-imprimir * {
		visibility: hidden !important;
	}
	body.imprimiendo-gestor .gestor-ot {
		position: absolute;
		left: 0;
		top: 0;
		width: 100%;
	}
}
</style>
